(function () {
  "use strict";

  if (window.MarineMaxCoveoFilterState) return;

  const FILTER_SELECTION_SEPARATOR = "\u001E";
  const REQUIRED_DEPENDENCIES = [
    "facetItemRawValue",
    "findFacetController",
    "flattenFacetControllers",
    "getFacetDisplayLabel",
    "normalizeFacetStateValues",
    "shouldSuppressFacetValue",
  ];

  function createCoveoFilterState(dependencies) {
    const deps = dependencies || {};
    const missingDependencies = REQUIRED_DEPENDENCIES.filter(function (name) {
      return typeof deps[name] !== "function";
    });
    if (missingDependencies.length) {
      throw new Error(
        `[Coveo Filter State] Missing dependencies: ${missingDependencies.join(", ")}`,
      );
    }

    const {
      facetItemRawValue,
      findFacetController,
      flattenFacetControllers,
      getFacetDisplayLabel,
      normalizeFacetStateValues,
      shouldSuppressFacetValue,
    } = deps;

    function buildFacetSelectionSnapshot(facetGen) {
      const snapshot = new Map();
      if (!facetGen) return snapshot;
      flattenFacetControllers(facetGen).forEach(function (facet) {
        const facetId = facet.facetId || facet.state?.facetId || "";
        if (!facetId) return;
        const facetLabel = getFacetDisplayLabel(
          facetId,
          String(
            facet.state?.displayName || facet.state?.label || facetId,
          ).trim(),
        );
        normalizeFacetStateValues(facet.state || {}).forEach(function (value) {
          const rawValue = String(facetItemRawValue(value));
          if (!rawValue) return;
          if (shouldSuppressFacetValue(facetId, facetLabel, rawValue)) return;
          snapshot.set(
            `${facetId}${FILTER_SELECTION_SEPARATOR}${rawValue}`,
            value.state === "selected",
          );
        });
      });
      return snapshot;
    }

    function buildDrawerDesiredSelectionSnapshot(content, facetGen) {
      const baseline =
        content?._coveoFilterSessionBaseline instanceof Map
          ? content._coveoFilterSessionBaseline
          : buildFacetSelectionSnapshot(facetGen);
      const snapshot = new Map(baseline);
      if (!content) return snapshot;
      content
        .querySelectorAll(
          'input[type="checkbox"][data-facet-id][data-value]:not(.coveo-mobile-range-hidden-checkbox)',
        )
        .forEach(function (checkbox) {
          const facetId = checkbox.getAttribute("data-facet-id");
          const value = checkbox.getAttribute("data-value");
          if (!facetId || value == null) return;
          snapshot.set(
            `${facetId}${FILTER_SELECTION_SEPARATOR}${String(value)}`,
            !!checkbox.checked,
          );
        });
      return snapshot;
    }

    function snapshotHasSelectedFacetMatching(snapshot, matcher) {
      if (!(snapshot instanceof Map) || typeof matcher !== "function") {
        return false;
      }
      for (const [key, isSelected] of snapshot.entries()) {
        if (!isSelected) continue;
        const separatorIndex = key.indexOf(FILTER_SELECTION_SEPARATOR);
        if (separatorIndex === -1) continue;
        if (matcher(key.slice(0, separatorIndex))) return true;
      }
      return false;
    }

    function facetSelectionSnapshotsMatch(desiredSnapshot, actualSnapshot) {
      if (!(desiredSnapshot instanceof Map) || !(actualSnapshot instanceof Map)) {
        return false;
      }
      for (const [key, desiredChecked] of desiredSnapshot.entries()) {
        if (!!actualSnapshot.get(key) !== !!desiredChecked) {
          return false;
        }
      }
      for (const [key, actualChecked] of actualSnapshot.entries()) {
        if (!desiredSnapshot.has(key) && !!actualChecked) {
          return false;
        }
      }
      return true;
    }

    function resetPendingFilterSessionState(content, facetGen) {
      if (!content) return;
      content._coveoPendingCheckboxState = new Map();
      content._mmPendingFilterDl = [];
      content._coveoFilterSessionBaseline = buildFacetSelectionSnapshot(facetGen);
    }

    function pruneSyncedPendingFilterState(content, facetGen) {
      if (!content || !facetGen) return;
      try {
        const pendingCheckboxes = content._coveoPendingCheckboxState;
        if (!(pendingCheckboxes instanceof Map) || pendingCheckboxes.size === 0) {
          if (content._coveoApplyingFilters) {
            content._coveoApplyingFilters = false;
          }
          return;
        }
        for (const [key, desiredChecked] of pendingCheckboxes.entries()) {
          const separatorIndex = key.indexOf(FILTER_SELECTION_SEPARATOR);
          if (separatorIndex === -1) continue;
          const facetId = key.slice(0, separatorIndex);
          const value = key.slice(separatorIndex + 1);
          const facet = findFacetController(facetGen, facetId);
          if (!facet) continue;
          const facetValues = normalizeFacetStateValues(facet.state || {});
          const matchingValue = facetValues.find(function (entry) {
            return String(facetItemRawValue(entry)) === String(value);
          });
          if (!matchingValue) continue;
          const currentlySelected = matchingValue.state === "selected";
          if (currentlySelected === !!desiredChecked) {
            pendingCheckboxes.delete(key);
          }
        }
        if (pendingCheckboxes.size === 0 && content._coveoApplyingFilters) {
          content._coveoApplyingFilters = false;
          content._coveoFilterSessionBaseline =
            buildFacetSelectionSnapshot(facetGen);
        }
      } catch (_) { }
    }

    return Object.freeze({
      buildDrawerDesiredSelectionSnapshot,
      buildFacetSelectionSnapshot,
      facetSelectionSnapshotsMatch,
      pruneSyncedPendingFilterState,
      resetPendingFilterSessionState,
      snapshotHasSelectedFacetMatching,
    });
  }

  Object.defineProperty(window, "MarineMaxCoveoFilterState", {
    configurable: false,
    enumerable: false,
    value: Object.freeze({
      create: createCoveoFilterState,
    }),
    writable: false,
  });
})();
